import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import React, { useState, type JSX } from 'react';
import { ERROR_MESSAGE } from '../../../components/common/Alert';
import PersonAnzeige from '../../../components/common/PersonAnzeige';
import SearchPersonUnternehmen from '../../../components/common/SearchPersonUnternehmen';
import { SPACING_BETWEEN_BOXES, SPACING_BETWEEN_COLUMNS, SPACING_BETWEEN_FORM_FIELDS } from '../../../components/common/spacings';
import WarningSign from '../../../components/common/WarningSign';
import { PersonEingabeMaske } from '../../../components/PersonEingabeMaske/PersonEingabeMaske';
import { YesNoFormControl } from '../../../components/YesNoFormControl';
import { aktionErlaubt } from '../../../domain/aktionErlaubt';
import { aktualisierePerson } from '../../../domain/aktualisierePerson';
import { isAenderbar } from '../../../domain/isAenderbar';
import { AKTION_KUNDENDATEN_ERFASSEN } from '../../../frontendConstants';
import { useUser } from '../../../hooks/useUser';
import { PersonenTyp } from '../../../shared/constants';
import { getMessageFromError } from '../../../shared/throw';
import { Person, Vorgang } from '../../../types';

type Props = {
  readonly vorgang: Vorgang;
  readonly aktualisiereVorgang: (vorgang: Partial<Vorgang>) => void;
  readonly speichereVorgang: (aenderungen?: Partial<Vorgang>) => void;
  readonly setzeVorgang: (vorgang: Vorgang) => void;
  readonly setLoading: (value: boolean) => void;
  readonly isLoading: boolean;
};

export function Fahrzeughalter({ vorgang, aktualisiereVorgang, speichereVorgang, setzeVorgang, setLoading, isLoading }: Props): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { gruppenVonMandant } = useUser();
  const [dialog, setDialog] = useState({ anlegen: false, aendern: false });

  const handleFahrzeughalterAusgewaehlt = (person: Person) => {
    speichereVorgang({ fahrzeughalterId: person.id });
  };

  const handleAktualisiereFahrzeughalter = (person: Person) => {
    setLoading(true);

    aktualisierePerson({
      ...person,
      mandant: vorgang.mandant,
      weitereBearbeiter: Array.from(new Set([...(person.weitereBearbeiter ?? []), ...vorgang.ehemaligeBearbeiter, vorgang.userId]))
    })
      .then((aktualisiertePerson) => {
        speichereVorgang({ fahrzeughalterId: aktualisiertePerson.id });
        setDialog({ anlegen: false, aendern: false });
      })
      .catch((error) => enqueueSnackbar(getMessageFromError(error) ?? 'Fahrzeughalter konnte nicht gespeichert werden', ERROR_MESSAGE))
      .finally(() => setLoading(false));
  };

  const handleFahrzeughalterAendernDialog = () => {
    setDialog((current) => ({ ...current, aendern: true }));
  };

  const handleFahrzeughalterAnlegenDialog = () => {
    setDialog((current) => ({ ...current, anlegen: true }));
  };

  const handleFahrzeughalterLoeschen = () => {
    speichereVorgang({
      fahrzeughalterId: null,
      fahrzeughalterIstAuftraggeber: true,
      fahrzeughalterRechtsschutzVorhanden: null
    });
  };

  const isDisabled =
    !vorgang || isLoading || !isAenderbar(vorgang) || !aktionErlaubt(AKTION_KUNDENDATEN_ERFASSEN, gruppenVonMandant(vorgang?.mandant || ''), vorgang?.status);

  return (
    <Paper
      sx={(theme) => ({
        padding: theme.spacing(SPACING_BETWEEN_BOXES)
      })}
    >
      <Grid container>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h6" gutterBottom>
            Fahrzeughalter
          </Typography>
        </Grid>
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={vorgang?.fahrzeughalterIstAuftraggeber ?? true}
                disabled={isDisabled}
                onChange={(event) => {
                  aktualisiereVorgang({
                    fahrzeughalterIstAuftraggeber: event.target.checked
                  });
                }}
                data-testid="fahrzeughalter-istAuftraggeber"
              />
            }
            label="wie Auftraggeber"
          />
        </Grid>
      </Grid>
      <Collapse
        component={Box}
        sx={(theme) => ({
          paddingTop: theme.spacing(2)
        })}
        in={!(vorgang?.fahrzeughalterIstAuftraggeber ?? true)}
      >
        <Grid container spacing={SPACING_BETWEEN_COLUMNS}>
          <Grid
            size={12}
            container
            sx={(theme) => ({
              [theme.breakpoints.down('md')]: {
                marginBottom: -theme.spacing(SPACING_BETWEEN_FORM_FIELDS)
              }
            })}
          >
            <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} size={12}>
              <Grid size={12}>
                <SearchPersonUnternehmen
                  mandantId={vorgang?.mandant}
                  isDisabled={isDisabled}
                  label="Fahrzeughalter suchen"
                  onSelect={(person) => handleFahrzeughalterAusgewaehlt(person as Person)}
                  testPraefix="fahrzeughalter"
                  personenUnternehmenTyp={PersonenTyp.PERSON}
                />
              </Grid>
              <Grid
                size={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <IconButton
                  disabled={isDisabled || !vorgang?.fahrzeughalterId}
                  onClick={handleFahrzeughalterAendernDialog}
                  data-testid="fahrzeughalter-bearbeiten"
                  size="large"
                >
                  <Tooltip title="Ändern">
                    <EditIcon />
                  </Tooltip>
                </IconButton>
                <IconButton disabled={isDisabled} onClick={handleFahrzeughalterAnlegenDialog} data-testid="fahrzeughalter-anlegen" size="large">
                  <Tooltip title="Anlegen">
                    <AddBoxIcon />
                  </Tooltip>
                </IconButton>
                <IconButton
                  disabled={isDisabled || !vorgang?.fahrzeughalterId}
                  onClick={handleFahrzeughalterLoeschen}
                  data-testid="fahrzeughalter-loeschen"
                  size="large"
                >
                  <Tooltip title="Zuorgnung löschen">
                    <DeleteIcon />
                  </Tooltip>
                </IconButton>
              </Grid>
              <Grid size={12}>
                <WarningSign
                  show={vorgang?.fahrzeughalter === null && vorgang?.fahrzeughalterId !== null}
                  text="Der Fahrzeughalter wurde gelöscht oder Sie haben nicht die erforderlichen Rechte, diesen zu sehen."
                />
                {vorgang?.fahrzeughalter && <PersonAnzeige person={vorgang.fahrzeughalter} testPraefix="fahrzeughalter" />}
              </Grid>
              {vorgang?.fahrzeughalter && (
                <Grid size={12}>
                  <YesNoFormControl
                    labelText="Rechtsschutz"
                    value={vorgang.fahrzeughalterRechtsschutzVorhanden}
                    disabled={isDisabled}
                    onChange={(jaNein) => {
                      aktualisiereVorgang({
                        fahrzeughalterRechtsschutzVorhanden: jaNein
                      });
                    }}
                    dataTestid="fahrzeughalter-rechtsschutzVorhanden"
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Collapse>
      {(dialog.anlegen || dialog.aendern) && (
        <PersonEingabeMaske
          personDatensatz={dialog.aendern ? vorgang?.fahrzeughalter : null}
          typ={PersonenTyp.PERSON}
          onClose={() => {
            setDialog({ anlegen: false, aendern: false });
          }}
          aktualisierePerson={handleAktualisiereFahrzeughalter}
          isDisabled={isDisabled}
          mandant={vorgang.mandant}
          testPraefix="fahrzeughalter"
        />
      )}
    </Paper>
  );
}
